<template>
  <div class="pa-setup">
    <div v-if="loading" class="pa-setup__loading">Loading your project setup…</div>
    <div v-else-if="loadError || !activeAudit" class="pa-setup__error">
      We couldn’t load your project setup. Try again in a moment.
    </div>

    <template v-else>
      <!-- mission-control: switch between this site's audits (same as the White Glove widget) -->
      <b-dropdown
        v-if="isAdmin && audits.length > 1"
        aria-role="list"
        class="pa-setup__switcher"
      >
        <template #trigger>
          <b-button icon-right="angle-down">
            Showing: <span class="font-weight-bold">{{ activeAuditLabel }}</span>
          </b-button>
        </template>
        <b-dropdown-item
          v-for="item in auditsDropdownItems"
          :key="item.key"
          @click="activeAuditId = item.key"
        >{{ item.label }}</b-dropdown-item>
      </b-dropdown>

      <!-- Step indicator (design WizSteps) -->
      <div class="pa-wsteps">
        <template v-for="(label, i) in stepLabels">
          <div :key="'step-' + i" class="pa-wstep" :class="'pa-wstep--' + stepState(i)">
            <div class="pa-wnum">
              <i v-if="stepState(i) === 'done'" class="material-icons">check</i>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <span class="pa-wstep__label">
              {{ label }}
              <span class="pa-wstep__mins">{{ stepDurations[i] }}</span>
            </span>
          </div>
          <div
            v-if="i < stepLabels.length - 1"
            :key="'line-' + i"
            class="pa-wline"
            :class="{ 'pa-wline--done': i < currentStep }"
          ></div>
        </template>
      </div>

      <div v-if="currentStep > 0" class="pa-planok">
        <i class="material-icons" aria-hidden="true">check_circle</i>
        <span>{{ paidBannerText }}</span>
      </div>

      <setup-fee-step
        v-if="shownStep === STEP.FEE"
        :key="'fee-' + activeAudit.id"
        :site="site"
        :active-audit="activeAudit"
        :package-info="setupPackage"
        :setup-invoice="setupInvoice"
        :is-admin="isAdmin"
        @audit-updated="onAuditUpdated"
        @continue="goForward"
      />
      <site-info-step
        v-else-if="shownStep === STEP.SITE_INFO"
        :key="'site-' + activeAudit.id"
        :site="site"
        :active-audit="activeAudit"
        @back="viewStep = STEP.FEE"
        @done="onStepDone"
        @continue="goForward"
      />
      <publishing-step
        v-else-if="shownStep === STEP.PUBLISHING"
        :key="'pub-' + activeAudit.id"
        :site="site"
        :active-audit="activeAudit"
        @back="viewStep = STEP.SITE_INFO"
        @done="onStepDone"
        @continue="goForward"
      />
      <google-step
        v-else-if="shownStep === STEP.GOOGLE"
        :key="'google-' + activeAudit.id"
        :site="site"
        :active-audit="activeAudit"
        :is-admin="isAdmin"
        @back="viewStep = STEP.PUBLISHING"
        @done="onStepDone"
      />
      <div v-else class="pa-setup__completing">
        <i class="material-icons" aria-hidden="true">check_circle</i>
        Setup complete. Opening your project…
      </div>
    </template>
  </div>
</template>

<script>
import SetupFeeStep from './SetupFeeStep.vue'
import SiteInfoStep from './SiteInfoStep.vue'
import PublishingStep from './PublishingStep.vue'
import GoogleStep from './GoogleStep.vue'
import {
  POP_AUTHORITY_SETUP_FEE_PACKAGE_KEY,
  POP_AUTHORITY_SETUP_STEP,
  popAuthoritySetupStep,
  showPopAuthorityToast,
} from '@/utils/popAuthority'
import { liveCheckMixin } from '../liveCheckMixin'

const STEP_LABELS = ['Setup fee', 'Tell us about your site', 'Publishing', 'Connect Google']
// The setup invoice is paid on Stripe's page, in another tab: while it is unpaid the wizard asks the
// server again every few seconds (liveCheckMixin). Stripe's webhook can land a few seconds after the
// payment, so one check on return is not enough.
const PAYMENT_CHECK_EVERY_MS = 4000
const PAYMENT_CHECK_FOR_MS = 10 * 60 * 1000
const UNPAID_INVOICE_STATUSES = ['in_checkout', 'in_cart_checkout']
const STEP_DURATIONS = ['about 1 min', 'about 5 min', 'about 3 min', 'about 2 min']

/**
 * POP Authority setup wizard (design: pa-setup.jsx SetupWizard).
 * 1 Setup fee → 2 Tell us about your site → 3 Publishing → 4 Connect Google.
 * The current step is derived from the audit row; `viewStep` lets the user revisit earlier steps
 * and edit them (design: the form stays editable during setup). Submitting the publishing step
 * again re-saves the answers, the publishing mode and the Asana onboarding task / audit task
 * description. Every mutation reloads the audits and emits `audits-changed` so the layout
 * re-fetches the project statuses (and swaps to the tabs once onboarding is complete).
 * The exception card (e.g. setup invoice overdue) is rendered by the host, above the title (design).
 */
export default {
  name: 'PopAuthoritySetupWizard',
  components: { SetupFeeStep, SiteInfoStep, PublishingStep, GoogleStep },
  mixins: [liveCheckMixin({ everyMs: PAYMENT_CHECK_EVERY_MS, forMs: PAYMENT_CHECK_FOR_MS })],
  props: {
    site: { type: Object, default: null },
    /** Per-site status row from GET /api/white-glove-orders/ `siteProjectStatuses` (or null). */
    status: { type: Object, default: null },
  },
  provide () {
    return {
      // Reused White Glove cards (GoogleAuthCodeButton hosts etc.) inject this toast helper
      wgToast: (message, action = null, kind = 'success') => {
        this.$notify({
          group: 'info',
          type: kind === 'error' ? 'error' : 'success',
          text: message,
        })
      },
    }
  },
  data () {
    return {
      STEP: POP_AUTHORITY_SETUP_STEP,
      loading: true,
      loadedOnce: false,
      loadError: false,
      audits: [],
      activeAuditId: null,
      packages: {},
      viewStep: null,
    }
  },
  computed: {
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isAdmin () {
      return this.isMissionControlSession && Boolean(this.$store.state.isWhiteGloveAdminMode)
    },
    activeAudit () {
      if (!this.activeAuditId) return null
      return this.audits.find((audit) => audit.id === this.activeAuditId) || null
    },
    /** An invoice is out (emailed, or a checkout was opened) and the setup fee is not paid yet. */
    awaitingPayment () {
      const audit = this.activeAudit
      if (!audit || audit.order_status === 'paid') return false
      return Boolean(audit.stripe_hosted_invoice_url) || UNPAID_INVOICE_STATUSES.includes(audit.order_status)
    },
    liveCheckWanted () {
      return this.awaitingPayment
    },
    auditsDropdownItems () {
      return this.audits.map((audit) => ({
        key: audit.id,
        label: audit.order_status === 'user_editing' ? 'New setup' : `Setup ${audit.id}`,
      }))
    },
    activeAuditLabel () {
      const item = this.auditsDropdownItems.find((i) => i.key === this.activeAuditId)
      return item ? item.label : '-'
    },
    /**
     * The wizard only exists for POP Authority sites, so the card always describes the setup fee
     * (the backend stamps the same package key on the audit draft when it lists the audits).
     */
    setupPackage () {
      return (
        this.packages[POP_AUTHORITY_SETUP_FEE_PACKAGE_KEY] ||
        { key: POP_AUTHORITY_SETUP_FEE_PACKAGE_KEY, label: 'POP Authority setup fee', price: 375 }
      )
    },
    currentStep () {
      return popAuthoritySetupStep(this.activeAudit)
    },
    shownStep () {
      if (this.viewStep === null) return this.currentStep
      return Math.min(this.viewStep, this.currentStep)
    },
    stepLabels: () => STEP_LABELS,
    stepDurations: () => STEP_DURATIONS,
    setupInvoice () {
      const ctx = this.status && this.status.template_context
      return (ctx && ctx.setup_invoice) || null
    },
    /**
     * Design pa-setup.jsx: "Setup fee paid: $375.00[ with coupon CODE (50% off)], includes your full content
     * audit". An admin "mark paid" records $0 without a coupon: the list price is shown, as in the design.
     */
    paidBannerText () {
      const audit = this.activeAudit || {}
      const price = Number(audit.price_on_checkout)
      const before = Number(audit.price_on_checkout_before_discount)
      const hasCoupon = Number.isFinite(price) && Number.isFinite(before) && before > price
      const amount = Number.isFinite(price) && (price > 0 || hasCoupon) ? price : Number(this.setupPackage.price) || 0
      let text = `Setup fee paid: $${amount.toFixed(2)}`
      if (hasCoupon) {
        const off = Number(audit.applied_coupon_amount_off)
        const label = audit.applied_coupon_type === 'Percent Off' && Number.isFinite(off)
          ? `${off}% off`
          : `$${(before - price).toFixed(2)} off`
        text += ` with coupon${audit.applied_coupon ? ` ${audit.applied_coupon}` : ''} (${label})`
      }
      return text + ', includes your full content audit'
    },
  },
  watch: {
    'site.id': {
      handler (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.loadData(true)
        }
      },
      immediate: true,
    },
    '$store.state.refreshDataAfterCartPurchase' () {
      this.loadData()
    },
    currentStep () {
      // a step just completed: show the new current step
      this.viewStep = null
    },
  },
  methods: {
    /** Quiet check (liveCheckMixin): only the audits are read; the wizard reloads once the invoice shows as paid. */
    liveCheck () {
      if (!this.site || !this.site.id) return null
      const auditId = this.activeAuditId
      return this.$store.dispatch('getSiteContentAudits', { siteId: this.site.id })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') return null
          const fresh = (data.data || []).find((audit) => audit.id === auditId)
          if (!fresh || fresh.order_status !== 'paid') return null
          showPopAuthorityToast(this, 'Payment received. Thank you!')
          return this.loadData()
        })
    },
    stepState (i) {
      if (i === this.shownStep) return 'cur'
      return i < this.currentStep ? 'done' : 'idle'
    },
    loadData (resetActiveAuditId = false) {
      if (!this.site || !this.site.id) return Promise.resolve()
      const initial = !this.loadedOnce
      return this.$store.dispatch('getSiteContentAudits', { siteId: this.site.id })
        .then((response) => {
          if (!(response.data && response.data.status === 'SUCCESS')) {
            throw new Error('Could not load the audits')
          }
          this.audits = response.data.data || []
          const stillThere = this.audits.some((a) => a.id === this.activeAuditId)
          if (resetActiveAuditId || !stillThere) {
            this.activeAuditId = this.audits.length ? this.audits[0].id : null
          }
          return this.$store.dispatch('getAvailableWhiteGloveAddonPackages')
        })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.packages = Object.fromEntries((response.data.packages || []).map((p) => [p.key, p]))
          }
          this.loadError = false
          if (!initial) {
            this.$emit('audits-changed', this.audits)
          }
        })
        .catch(() => {
          this.loadError = true
        })
        .finally(() => {
          this.loading = false
          this.loadedOnce = true
        })
    },
    onAuditUpdated () {
      return this.loadData()
    },
    onStepDone () {
      this.viewStep = null
      return this.loadData()
    },
    /** "Next" on a step that is already done: return to the current step. */
    goForward () {
      this.viewStep = null
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup.scss';

.pa-setup {
  max-width: 960px;
  color: $pa-teal;

  &__loading,
  &__error {
    font-size: 0.95rem;
    margin: 16px 0;
  }

  &__switcher {
    margin-bottom: 12px;
  }

  &__completing {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
    margin: 16px 0;

    .material-icons {
      color: $pa-success;
    }
  }
}

/* Step indicator (pa.css .pa-wsteps) */
.pa-wsteps {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 18px 0 22px;
  flex-wrap: wrap;
}

.pa-wstep {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 800;
  font-size: 1rem;
  color: $pa-teal;

  &__label {
    display: inline-block;
  }

  &__mins {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    color: $pa-faint;
  }

  &--idle {
    color: $pa-faint;
  }

  &--done {
    color: $pa-success;
  }
}

.pa-wnum {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: $pa-box-bg;
  color: $pa-faint;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  flex: none;

  .material-icons {
    font-size: 18px;
  }

  .pa-wstep--cur & {
    background: $pa-accent;
    color: #fff;
  }

  .pa-wstep--done & {
    background: $pa-success;
    color: #fff;
  }
}

.pa-wline {
  width: 52px;
  height: 2px;
  background: $pa-border-soft;
  flex: none;

  &--done {
    background: $pa-success;
  }
}

.pa-planok {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #c4eddc;
  background: #effbf5;
  border-radius: 10px;
  padding: 12px 16px;
  font-weight: 700;
  margin-bottom: 18px;

  .material-icons {
    color: $pa-success;
  }
}
</style>
